/**
 * WebCardHost — a feed slot containing an uploaded HTML page instead of a wasm game.
 *
 * Deliberately mirrors GameHost's surface (load / resume / pause / exit / destroy plus the
 * same events) so the carousel drives both without knowing which it holds. What differs is
 * everything underneath: no worker, no OffscreenCanvas, no SharedArrayBuffer — just an iframe
 * with the page inside it.
 *
 * WHAT THIS HOST CAN AND CANNOT MEASURE
 * Pointer events inside a sandboxed cross-origin frame never reach the parent document, and
 * they cannot be synthesised back in. So the host measures the outside of the card only:
 * impression, dwell, and how the session ended. That is the class-agnostic set the
 * interactive-vs-passive comparison actually needs, and it is the same set a clip or thread
 * card can produce — which is the point.
 *
 * A card MAY additionally postMessage its own events (see BlitzCardMessage). Those are
 * enrichment and diagnostics only. They are content-reported, so per the telemetry doctrine
 * they must never feed ranking or payouts: a card that wants to look engaging can simply lie.
 * The host keeps them in separate fields for exactly that reason.
 *
 * SANDBOXING
 * Two kinds of page sit in this frame, and they get different sandbox tokens (sandboxTokensFor):
 *   hosted   — HTML we serve from our own origin (/cards/*, blob: imports). `allow-scripts` only,
 *              so it runs in an opaque origin and cannot reach our DOM, storage or cookies.
 *   external — a linked site on another origin. `allow-scripts allow-same-origin`, because the
 *              same-origin policy already walls it off from us, and an opaque origin breaks real
 *              sites (verified: a linked SPA threw on navigator.serviceWorker and painted nothing;
 *              with its own origin it painted fully).
 * Neither ever gets allow-forms, allow-popups or allow-top-navigation: a card cannot post
 * credentials anywhere or navigate the feed away.
 * The same origin rule (sourceOf) decides the idle policy: hosted pages keep the carousel's
 * 20 s sweep, external pages have no timer and never end IDLE_TIMEOUT — docs/DESIGN-NOTES.md
 * section 5.
 *
 * Whether an external site CAN be framed is decided by its own headers (X-Frame-Options,
 * frame-ancestors). A refusal still fires the frame's `load` event and paints nothing, and a
 * cross-origin frame cannot be inspected, so this host cannot tell. That check belongs at
 * submission time, server-side, where the headers can be read.
 *
 * LEAVING THE SCREEN
 * A page in an iframe cannot be paused. Its timers, its video and its audio keep going behind
 * whatever card comes next (Deepstate's soundtrack was still playing on the chess card), and
 * there is no attribute or message that mutes a cross-origin frame. So when the card leaves
 * the screen the carousel calls sleep(), which unloads the frame (src = about:blank), and the
 * next resume() loads the page again. To the carousel the slot is still the kept N-1 host in
 * state 'paused'; to the page it is a fresh start, which is the honest thing — a page we
 * cannot pause is a page we cannot resume either.
 */

import type { GameMeta, HostState, ReadyMessage } from './protocol';
import type { ExitCondition, LiveStats, MechanicalTelemetry } from '../telemetry/TelemetryTypes';
import { nextHostSerial } from './ContentHost';
import { loadCardState, saveCardState } from './cardState';
import { frameUrlFor } from '../api/cards';

/** Messages a card may voluntarily post upward. Untrusted: shape-checked, never load-bearing. */
export interface BlitzCardMessage {
  /**
   * 'touch': a person did something in the page (counted, and it keeps the session alive).
   * 'alive': the page is doing something on its own — a clip playing — so the idle clock is
   * kept fresh without it counting as a touch.
   * 'load' / 'save': the save channel (src/lib/cardState.ts). 'load' asks for the state this
   * card saved on this device and is answered with {blitz:'state', data}; 'save' carries the
   * page's JSON string. Neither is counted, and neither reaches telemetry.
   */
  blitz: 'ready' | 'touch' | 'beat' | 'alive' | 'load' | 'save';
  /** Optional ordinal for 'beat'. */
  id?: number;
  /** For 'save': the state as a JSON string, at most 4096 bytes. */
  data?: unknown;
}

/**
 * Sandbox tokens for a page URL. `allow-same-origin` is only dangerous when the framed
 * document shares OUR origin (it could then reach the parent and strip its own sandbox), so a
 * page that resolves to our origin — or to no origin at all (data:, about:) — gets an opaque
 * one, and a page on any other origin keeps its own.
 */
export function sandboxTokensFor(pageUrl: string, ownOrigin: string = location.origin): string[] {
  return isExternalPage(pageUrl, ownOrigin) ? ['allow-scripts', 'allow-same-origin'] : ['allow-scripts'];
}

/**
 * True when the page lives on an origin that is not ours: a linked site, or the mirror on the
 * deploy's second origin. A URL with no origin (data:, about:) or that does not parse is ours.
 * The one origin rule behind both the sandbox tokens and the idle policy.
 */
export function isExternalPage(pageUrl: string, ownOrigin: string = location.origin): boolean {
  let origin: string | null;
  try {
    origin = new URL(pageUrl, ownOrigin).origin;
  } catch {
    origin = null;
  }
  return origin !== null && origin !== 'null' && origin !== ownOrigin;
}

/**
 * Where a card comes from, for the idle policy. The registry says so (CardRow.source through
 * cardRowToMeta); a record without it — the static feed, a toolbar import — is judged by the
 * page it loads: no page is a wasm card, a page on our origin is hosted, anything else is
 * external. A mirrored page is external whatever origin the mirror serves it from: the site
 * never posts touches. Same origin rule as sandboxTokensFor, so the two never disagree.
 */
export function sourceOf(
  meta: Pick<GameMeta, 'kind' | 'page_url' | 'embed_mode' | 'source'>,
  ownOrigin: string = location.origin,
): 'wasm' | 'hosted' | 'external' {
  if (meta.source) return meta.source;
  if (meta.kind !== 'web') return 'wasm';
  if (meta.embed_mode === 'mirror') return 'external';
  return meta.page_url && isExternalPage(meta.page_url, ownOrigin) ? 'external' : 'hosted';
}

export interface WebCardHostOptions {
  userId: string;
  debug?: boolean;
  /** epoch-ms clock; injectable for tests */
  now?: () => number;
}

type EventName = 'state' | 'ready' | 'stats' | 'telemetry' | 'error' | 'log' | 'haptic' | 'score';

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export class WebCardHost {
  readonly serial = nextHostSerial();
  readonly meta: GameMeta;
  readonly inputMode = 'postMessage' as const;

  state: HostState = 'idle';
  sessionId = '';
  ready: ReadyMessage | null = null;
  lastStats: LiveStats | null = null;
  lastError: { phase: string; message: string } | null = null;
  score = 0;
  liked = false;
  loadStartedAt = 0;
  readyAt = 0;
  /**
   * performance.now() units, NOT epoch — the carousel's idle check compares this against
   * performance.now() directly, and GameHost stores it the same way. Mixing the two clocks
   * silently makes a card either never idle or instantly idle.
   */
  lastInputAt = 0;

  private frame: HTMLIFrameElement | null = null;
  /** The page's address, kept so a slept frame can be loaded again. */
  private url: string | null = null;
  /** True while the frame is blank because the card left the screen (see sleep()). */
  private dormant = false;
  /** Cancels a navigate() still waiting on the frame ('slept' or 'destroyed'), timer included. */
  private pendingCancel: ((why: 'slept' | 'destroyed') => void) | null = null;
  private listeners: Record<string, Set<(...a: never[]) => void>> = {};
  private readonly opts: WebCardHostOptions;
  private readonly now: () => number;

  // Host-measured session accounting.
  private sessionStart = 0;
  private activeMs = 0;
  private lastResumeAt = 0;
  private batchSeq = 0;
  private sessionOpen = false;
  private batchTimer: ReturnType<typeof setInterval> | null = null;

  // Content-reported, kept apart from anything that ranks or pays.
  private reportedTouches = 0;
  private reportedBeats = 0;

  private onWindowMessage = (ev: MessageEvent) => {
    // Only accept messages from OUR frame. An opaque-origin frame posts with origin "null",
    // so the source check is the one that means anything here.
    if (!this.frame || ev.source !== this.frame.contentWindow) return;
    const d = ev.data as BlitzCardMessage | undefined;
    if (!d || typeof d !== 'object' || typeof d.blitz !== 'string') return;
    if (d.blitz === 'touch') {
      this.reportedTouches++;
      this.lastInputAt = performance.now();
    } else if (d.blitz === 'alive') {
      // Content-reported, like everything here: it keeps the idle clock fresh and nothing else.
      this.lastInputAt = performance.now();
    } else if (d.blitz === 'beat') {
      this.reportedBeats++;
    } else if (d.blitz === 'load' || d.blitz === 'save') {
      // The save channel is for pages we host (the html viewer asks on every start). A linked
      // site has storage on its own origin and is never answered.
      if (sourceOf(this.meta) === 'external') return;
      if (d.blitz === 'save') {
        saveCardState(this.meta.id, d.data);
        return;
      }
      // Keyed by this host's card id, never by anything in the message, so a card only ever
      // gets back what it saved itself. An opaque-origin frame can only be addressed with '*'.
      try {
        this.frame.contentWindow?.postMessage({ blitz: 'state', data: loadCardState(this.meta.id) }, '*');
      } catch {
        /* a frame going away as it asked */
      }
    }
  };

  constructor(meta: GameMeta, opts: WebCardHostOptions) {
    this.meta = meta;
    this.opts = opts;
    this.now = opts.now ?? (() => performance.timeOrigin + performance.now());
  }

  /**
   * Always false: a web card has no Web Worker. This feeds the debug API's liveWorkers count,
   * which exists to police the 3-worker memory cap, so reporting true here would inflate the
   * count with hosts that own no worker at all. Whether the card still holds its surface is a
   * different question, answered by frameAlive.
   */
  get workerAlive(): boolean {
    return false;
  }

  /** Whether the iframe is still attached — the web card's equivalent of a live worker. */
  get frameAlive(): boolean {
    return this.frame !== null;
  }

  on(event: EventName, fn: (...a: never[]) => void): () => void {
    const set = (this.listeners[event] ??= new Set());
    set.add(fn);
    return () => set.delete(fn);
  }

  private emit(event: EventName, ...args: unknown[]) {
    const set = this.listeners[event];
    if (!set) return;
    for (const fn of set) (fn as (...a: unknown[]) => void)(...args);
  }

  private setState(s: HostState) {
    if (this.state === s) return;
    if (this.state === 'destroyed') return; // nothing comes back from destroyed
    this.state = s;
    this.emit('state', s, this);
  }

  /** The slide hands over the iframe it rendered. Analogous to attachCanvases(). */
  attachFrame(frame: HTMLIFrameElement) {
    if (this.frame === frame) return;
    this.frame = frame;
    // A slide rebuilt under a live host (the feed list was swapped): the old frame is gone
    // with whatever was loading in it. Lock the new one down the same way and mark the host
    // dormant, so the next resume()/warm() loads the page into the frame that is on screen.
    if (this.url) {
      this.pendingCancel?.('destroyed');
      frame.setAttribute('sandbox', sandboxTokensFor(this.url).join(' '));
      frame.setAttribute('referrerpolicy', 'no-referrer');
      this.dormant = true;
      if (this.state === 'loading' || this.state === 'running' || this.state === 'ready') this.setState('paused');
    }
  }

  async load(): Promise<ReadyMessage> {
    if (this.state !== 'idle') throw new Error(`load() called in state ${this.state}`);
    if (!this.frame) throw new Error('attachFrame() before load()');
    const url = frameUrlFor(this.meta) ?? this.meta.wasm_url;
    if (!url) throw new Error('web card has no page_url');

    this.url = url;
    const sandbox = sandboxTokensFor(url);
    this.frame.setAttribute('sandbox', sandbox.join(' '));
    this.frame.setAttribute('referrerpolicy', 'no-referrer');
    addEventListener('message', this.onWindowMessage);
    // Put to sleep before it ever loaded (the fresh N-1 after a step back): nobody is looking,
    // so the page is not fetched at all. resume() loads it when the card is next on screen.
    if (this.dormant) {
      this.setState('paused');
      throw new Error('slept');
    }
    return this.navigate(url, sandbox);
  }

  /**
   * Point the frame at the page and wait for it. Shared by load() and by the wake after
   * sleep(): both end in state 'ready', and the carousel's state handler resumes a ready host
   * that is the active card.
   */
  private async navigate(url: string, sandbox: string[]): Promise<ReadyMessage> {
    if (!this.frame) throw new Error('attachFrame() before load()');
    this.setState('loading');
    this.loadStartedAt = performance.now();
    const frame = this.frame;

    const info = await new Promise<ReadyMessage>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error(`web card ${this.meta.id} did not load within 10s`));
      }, 10_000);
      const onLoad = () => {
        cleanup();
        // The frame was blanked underneath this load (the card left the screen while the
        // page was still coming): what just loaded is about:blank, not the page.
        if (this.dormant) {
          reject(new Error('slept'));
          return;
        }
        resolve({
          type: 'ready',
          gameId: this.meta.id,
          compileMs: 0,
          instantiateMs: 0,
          initMs: 0,
          firstFrameMs: Math.round(performance.now() - this.loadStartedAt),
          wasmBytes: 0,
          imports: { total: 0, missing: [] },
          gl: 'none',
          sandbox: ['iframe', ...sandbox],
          inputMode: 'postMessage',
        });
      };
      const onErr = () => {
        cleanup();
        reject(new Error(`web card ${this.meta.id} failed to load`));
      };
      const cleanup = () => {
        clearTimeout(timer);
        frame.removeEventListener('load', onLoad);
        frame.removeEventListener('error', onErr);
        this.pendingCancel = null;
      };
      this.pendingCancel = (why) => {
        cleanup();
        reject(new Error(why));
      };
      frame.addEventListener('load', onLoad);
      frame.addEventListener('error', onErr);
      frame.src = url;
    }).catch((err: Error) => {
      // Slept mid-load: not a failure, the card simply left. It rests as a dormant 'paused'
      // host and loads again when it is next on screen. Destroyed: nothing to record.
      if (err.message === 'slept') this.setState('paused');
      else if (err.message !== 'destroyed') this.fail('load', err.message);
      throw err;
    });

    if ((this.state as HostState) !== 'loading') throw new Error('destroyed during load');
    this.ready = info;
    this.readyAt = performance.now();
    this.setState('ready');
    this.emit('ready', info, this);
    return info;
  }

  /**
   * The card has left the screen. Unload the page: there is no way to pause a document in a
   * cross-origin frame, and left alone its audio and timers run on behind the next card. The
   * host stays 'paused' (the carousel's kept N-1 slot); resume() loads the page again.
   */
  sleep() {
    // No state gate: a 'crashed' host (a slow site that beat the 10 s load timer) still has a
    // live page in its frame, and a 'loading' one is about to. Before load() (no url yet) the
    // sleep still sticks, so load() does not fetch a page nobody is looking at.
    if (this.dormant || this.state === 'destroyed') return;
    if (this.state === 'running') this.pause('swipe');
    this.dormant = true;
    // A load still on its way is cancelled now, synchronously — its listeners come off and
    // the state settles to 'paused' here, so the carousel's next pass (warm(), resume()) sees
    // a host it can act on rather than one still 'loading' a page that is no longer there.
    this.pendingCancel?.('slept');
    if (this.state === 'loading') this.setState('paused');
    if (this.frame && this.url) this.frame.src = 'about:blank';
  }

  /** Whether the frame is currently blank because the card is off screen. */
  get asleep(): boolean {
    return this.dormant;
  }

  /**
   * Load a dormant page again WITHOUT starting a session: for the N+1 slot, so that BLITZ
   * onto a card you just came back from is as warm as the slot policy promises. A freshly
   * navigated cross-origin document has had no gesture, so it cannot be playing anything.
   */
  warm() {
    if (!this.dormant || (this.state !== 'paused' && this.state !== 'ready')) return;
    this.dormant = false;
    const url = this.url ?? '';
    void this.navigate(url, sandboxTokensFor(url)).catch(() => {
      /* recorded by navigate() */
    });
  }

  resume() {
    if (this.state !== 'ready' && this.state !== 'paused') return;
    if (this.dormant) {
      // Back on screen after sleep(): load the page again. The session opens NOW, not when
      // the page arrives: the person is looking at this card, and if they press BLITZ before
      // it loads that is a bounce the ranker must see, as it would for a wasm card. navigate()
      // ends in 'ready', the carousel resumes the active ready host, and that resume()
      // continues this same session (sessionOpen is already true) into 'running'.
      this.dormant = false;
      this.openSession();
      const url = this.url ?? '';
      void this.navigate(url, sandboxTokensFor(url)).catch(() => {
        /* recorded by navigate() */
      });
      return;
    }
    this.openSession();
    // A clock already running (the wake reload opened the session before the page came) is
    // not restarted: the wait was part of this visit, as it is on the bounce path.
    if (!this.lastResumeAt) this.lastResumeAt = this.now();
    // Start the idle clock at entry. Left at 0 it reads as "no input since the epoch", so the
    // idle sweep ends the session on its first tick.
    //
    // KNOWN LIMITATION: a web card that reports nothing will still idle out after the normal
    // timeout even while someone is happily playing, because input inside a sandboxed frame is
    // not observable from here. A page loaded directly in this frame can keep its session alive
    // by posting {blitz:'touch'} (or {blitz:'alive'} while something plays); a page inside the
    // html upload viewer reaches us only through the viewer's relay, which passes touch, beat
    // and save. The proper fix is that SDK message, not a longer timeout. This applies to
    // hosted pages only: an external site is exempt from the sweep altogether (sourceOf ===
    // 'external', read by the carousel), because it never posts either.
    this.lastInputAt = performance.now();
    this.setState('running');
    // Same 3s cadence as the wasm agent so batches from both classes interleave comparably.
    this.batchTimer ??= setInterval(() => this.emitBatch('IN_PROGRESS'), 3000);
  }

  /** Mint a session if none is open; a resume that continues one leaves it alone. */
  private openSession() {
    if (this.sessionOpen) return;
    this.sessionId = uuid();
    this.sessionStart = this.now();
    this.activeMs = 0;
    this.batchSeq = 0;
    this.reportedTouches = 0;
    this.reportedBeats = 0;
    this.sessionOpen = true;
    this.lastResumeAt = this.now();
  }

  pause(_reason: 'swipe' | 'idle' | 'hidden' | 'manual') {
    if (this.state !== 'running') return;
    this.accrue();
    this.stopTimer();
    this.setState('paused');
    this.emitBatch('IN_PROGRESS');
  }

  exit(condition: ExitCondition) {
    if (!this.sessionOpen) return;
    this.accrue();
    this.stopTimer();
    this.emitBatch(condition);
    this.sessionOpen = false;
    if (this.state === 'running') this.setState('paused');
  }

  destroy() {
    if (this.sessionOpen) this.exit('DESTROYED');
    this.stopTimer();
    // A page still on its way must not report in later: its 10 s timer would flip this host
    // from 'destroyed' to 'crashed' and toast over whatever card is on screen by then.
    this.pendingCancel?.('destroyed');
    removeEventListener('message', this.onWindowMessage);
    if (this.frame) {
      // Blank it before dropping the reference: an iframe left with a src can keep running
      // its timers for a tick, which is the leak equivalent of not terminating a worker.
      this.frame.src = 'about:blank';
      this.frame = null;
    }
    this.dormant = false;
    this.setState('destroyed');
  }

  // ---- wasm-only surface, present so the carousel does not have to branch ----------------
  // These are not stubs hiding missing work: a page in an iframe receives pointer and sensor
  // events from the browser directly, and resizes with CSS. There is nothing for the host to
  // forward, and forwarding would mean synthesising events into a frame we cannot reach.

  /** No-op: the page gets real pointer events itself. Returns false — nothing was enqueued. */
  input(_kind: number, _id: number, _x: number, _y: number, _t?: number): boolean {
    return false;
  }

  /** No-op: a page reads deviceorientation itself if it wants it. */
  gyro(_beta: number, _gamma: number, _alpha: number): void {}

  /** No-op: the iframe is sized by CSS, so a resize needs no message. */
  resize(_width: number, _height: number, _dpr: number): void {}

  /** Always zero: there is no input queue to drop from. */
  get droppedInputs(): number {
    return 0;
  }

  /** Dev-toolbar affordance: prove containment works for this class too. */
  debugCrash() {
    this.fail('debug', 'simulated web card crash');
  }

  private accrue() {
    if (this.lastResumeAt) {
      this.activeMs += Math.max(0, this.now() - this.lastResumeAt);
      this.lastResumeAt = 0;
    }
  }

  private stopTimer() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  private fail(phase: string, message: string) {
    if (this.state === 'destroyed') return;
    this.lastError = { phase, message };
    // A session that was open (a wake reload that never arrived) ends here, as CRASH, with the
    // time spent waiting — not later, as a minutes-long DESTROYED when the slot is evicted.
    if (this.sessionOpen) this.exit('CRASH');
    this.setState('crashed');
    this.emit('error', message, phase, this);
  }

  /**
   * A batch shaped like the wasm agent's, so both classes land in one table and can be
   * compared. The game-specific columns are zero because there is genuinely nothing to put in
   * them — not because measurement failed.
   */
  private emitBatch(exit: ExitCondition) {
    const at = this.now();
    const live = this.activeMs + (this.lastResumeAt ? at - this.lastResumeAt : 0);
    const batch: MechanicalTelemetry = {
      session_uuid: this.sessionId || uuid(),
      user_id: this.opts.userId,
      game_id: this.meta.id,
      card_class: this.meta.class ?? 'web',
      referrer_user_id: null, // stamped by the transport
      entry_surface: 'direct', // stamped by the transport
      timestamp_ms: Math.round(this.sessionStart),
      actions_per_minute: 0,
      avg_decision_latency_ms: 0,
      fail_state_count: 0,
      micro_session_duration_ms: Math.round(live),
      exit_condition: exit,
      ui_interaction_density: 0,
      // Content-reported. Present for diagnostics; must not feed ranking or payouts.
      action_count: this.reportedTouches,
      input_event_count: this.reportedTouches,
      decision_samples: 0,
      p50_decision_latency_ms: 0,
      avg_fps: 0,
      avg_input_latency_ms: 0,
      avg_pipeline_latency_ms: 0,
      p95_pipeline_latency_ms: 0,
      input_mode: 'postMessage',
      score: this.reportedBeats,
      batch_seq: this.batchSeq++,
      is_final: exit !== 'IN_PROGRESS',
      client_ts_ms: Math.round(at),
    };
    this.emit('telemetry', batch, this);
  }
}
